import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  useCreateAcordo,
  useEntidades,
  type AcordoTipo,
  type CreateAcordoInput,
} from '@/lib/hooks/entidades';

interface FormValues {
  entidadeId: string;
  tipo: AcordoTipo | '';
  numero: string;
  orgaoConcedente: string;
  objeto: string;
  valorTotal: string;
  valorRepasse: string;
  valorContrapartida: string;
  dataInicio: string;
  dataFim: string;
}

interface Props {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DEFAULTS: FormValues = {
  entidadeId: '',
  tipo: '',
  numero: '',
  orgaoConcedente: '',
  objeto: '',
  valorTotal: '',
  valorRepasse: '',
  valorContrapartida: '',
  dataInicio: '',
  dataFim: '',
};

const TIPOS: { value: AcordoTipo; label: string }[] = [
  { value: 'COLABORACAO', label: 'Colaboração' },
  { value: 'FOMENTO', label: 'Fomento' },
  { value: 'COOPERACAO', label: 'Cooperação' },
  { value: 'AUXILIO', label: 'Auxílio' },
  { value: 'SUBVENCAO', label: 'Subvenção' },
];

const MONEY_PATTERN = /^\d+([.,]\d{1,2})?$/;

export function AcordoDrawer({ workspaceId, open, onOpenChange }: Props) {
  const createMut = useCreateAcordo(workspaceId);
  const { data: entidadesData } = useEntidades(workspaceId);
  const entidades = entidadesData?.items ?? [];

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: DEFAULTS });

  useEffect(() => {
    if (!open) reset(DEFAULTS);
  }, [open, reset]);

  async function onSubmit(values: FormValues) {
    if (!values.tipo) {
      toast.error('Selecione o tipo do acordo.');
      return;
    }
    if (!values.entidadeId) {
      toast.error('Selecione a entidade.');
      return;
    }

    const payload: CreateAcordoInput = {
      entidadeId: values.entidadeId,
      tipo: values.tipo,
      numero: values.numero,
      orgaoConcedente: values.orgaoConcedente,
      objeto: values.objeto,
    };
    if (values.valorTotal) payload.valorTotal = values.valorTotal.replace(',', '.');
    if (values.valorRepasse) payload.valorRepasse = values.valorRepasse.replace(',', '.');
    if (values.valorContrapartida)
      payload.valorContrapartida = values.valorContrapartida.replace(',', '.');
    if (values.dataInicio) payload.dataInicio = values.dataInicio;
    if (values.dataFim) payload.dataFim = values.dataFim;

    try {
      await createMut.mutateAsync(payload);
      toast.success('Acordo cadastrado.');
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao cadastrar acordo.');
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <SheetHeader
            eyebrow="Novo acordo"
            title="Cadastrar acordo"
            description="Convênio, termo de colaboração ou instrumento similar."
          />
          <SheetBody className="space-y-5">
            <div>
              <Label className="mb-1.5 block">Entidade</Label>
              <Controller
                control={control}
                name="entidadeId"
                rules={{ required: 'Obrigatório' }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a entidade…" />
                    </SelectTrigger>
                    <SelectContent>
                      {entidades.length === 0 ? (
                        <SelectItem value="_empty" disabled>
                          Nenhuma entidade cadastrada
                        </SelectItem>
                      ) : (
                        entidades.map((e) => (
                          <SelectItem key={e.id} value={e.id}>
                            {e.nomeFantasia ?? e.razaoSocial}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.entidadeId && (
                <p className="text-[11px] text-warn mt-1">{errors.entidadeId.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1.5 block">Tipo</Label>
                <Controller
                  control={control}
                  name="tipo"
                  rules={{ required: 'Obrigatório' }}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione…" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIPOS.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                {errors.tipo && (
                  <p className="text-[11px] text-warn mt-1">{errors.tipo.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="numero" className="mb-1.5 block">Número</Label>
                <Input
                  id="numero"
                  placeholder="Ex.: 001/2024"
                  {...register('numero', { required: 'Obrigatório' })}
                />
                {errors.numero && (
                  <p className="text-[11px] text-warn mt-1">{errors.numero.message}</p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="orgaoConcedente" className="mb-1.5 block">Órgão concedente</Label>
              <Input
                id="orgaoConcedente"
                placeholder="Ex.: Prefeitura Municipal de Suzano"
                {...register('orgaoConcedente', {
                  required: 'Obrigatório',
                  minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                })}
              />
              {errors.orgaoConcedente && (
                <p className="text-[11px] text-warn mt-1">{errors.orgaoConcedente.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="objeto" className="mb-1.5 block">Objeto</Label>
              <Textarea
                id="objeto"
                placeholder="Descrição do objeto do acordo…"
                rows={3}
                {...register('objeto', {
                  required: 'Obrigatório',
                  minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                })}
              />
              {errors.objeto && (
                <p className="text-[11px] text-warn mt-1">{errors.objeto.message}</p>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="valorTotal" className="mb-1.5 block text-xs">
                  Valor total (R$)
                </Label>
                <Input
                  id="valorTotal"
                  inputMode="decimal"
                  placeholder="0.00"
                  {...register('valorTotal', {
                    pattern: { value: MONEY_PATTERN, message: 'Valor inválido' },
                  })}
                />
                {errors.valorTotal && (
                  <p className="text-[11px] text-warn mt-1">{errors.valorTotal.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="valorRepasse" className="mb-1.5 block text-xs">
                  Repasse (R$)
                </Label>
                <Input
                  id="valorRepasse"
                  inputMode="decimal"
                  placeholder="0.00"
                  {...register('valorRepasse', {
                    pattern: { value: MONEY_PATTERN, message: 'Valor inválido' },
                  })}
                />
                {errors.valorRepasse && (
                  <p className="text-[11px] text-warn mt-1">{errors.valorRepasse.message}</p>
                )}
              </div>
              <div>
                <Label htmlFor="valorContrapartida" className="mb-1.5 block text-xs">
                  Contrapartida (R$)
                </Label>
                <Input
                  id="valorContrapartida"
                  inputMode="decimal"
                  placeholder="0.00"
                  {...register('valorContrapartida', {
                    pattern: { value: MONEY_PATTERN, message: 'Valor inválido' },
                  })}
                />
                {errors.valorContrapartida && (
                  <p className="text-[11px] text-warn mt-1">{errors.valorContrapartida.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="dataInicio" className="mb-1.5 block">
                  Início <span className="text-fg-dim font-normal">(opcional)</span>
                </Label>
                <Input id="dataInicio" type="date" {...register('dataInicio')} />
              </div>
              <div>
                <Label htmlFor="dataFim" className="mb-1.5 block">
                  Fim <span className="text-fg-dim font-normal">(opcional)</span>
                </Label>
                <Input id="dataFim" type="date" {...register('dataFim')} />
              </div>
            </div>
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild>
              <Button variant="ghost" type="button">Cancelar</Button>
            </SheetClose>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                  Salvando…
                </>
              ) : (
                'Cadastrar acordo'
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
